import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MatchingJobStatus,
  PaymentCategorization,
  PaymentCategorizationPage,
  PaymentMatch,
  PaymentMatchPage,
  PaymentMatchStatus,
  PaymentMatchingSettings,
  ZohoBankAccount,
} from '../models/payment-matching.model';
import { RestUtils } from './rest-utils';

@Injectable()
export class PaymentMatchingService {
  http: HttpClient;
  utils = new RestUtils();
  headers = new HttpHeaders();

  constructor(http: HttpClient) {
    this.http = http;
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  getBankAccounts(): Observable<ZohoBankAccount[]> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/bank-accounts');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<ZohoBankAccount[]>(url, options);
  }

  getMatches(page: number, size: number, status?: PaymentMatchStatus): Observable<PaymentMatchPage> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/matches', { page, size, status });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<PaymentMatchPage>(url, options);
  }

  getCategorizations(page: number, size: number): Observable<PaymentCategorizationPage> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/categorizations', { page, size });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<PaymentCategorizationPage>(url, options);
  }

  syncBankAccounts(): Observable<ZohoBankAccount[]> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/bank-accounts/sync');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<ZohoBankAccount[]>(url, null, options);
  }

  toggleBankAccount(accountId: string, enabled: boolean): Observable<any> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/bank-accounts/${accountId}/toggle`, { enabled });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, null, options);
  }

  toggleBankAccountNearAmountMatching(accountId: string, enabled: boolean): Observable<any> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/bank-accounts/${accountId}/near-amount-matching`, { enabled });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, null, options);
  }

  toggleBankAccountCategorization(accountId: string, enabled: boolean): Observable<any> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/bank-accounts/${accountId}/categorization`, { enabled });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put(url, null, options);
  }

  runMatchingNow(): Observable<any> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/run');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post(url, null, options);
  }

  getJobStatus(): Observable<MatchingJobStatus> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/status');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<MatchingJobStatus>(url, options);
  }

  getSettings(): Observable<PaymentMatchingSettings> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/settings');
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<PaymentMatchingSettings>(url, options);
  }

  updateAutoMatching(enabled: boolean): Observable<PaymentMatchingSettings> {
    const url = this.utils.buildUrl('admin/usr/payment-matching/settings/auto-matching', { enabled });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.put<PaymentMatchingSettings>(url, null, options);
  }

  approveMatch(matchId: string): Observable<PaymentMatch> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/matches/${matchId}/approve`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<PaymentMatch>(url, null, options);
  }

  rejectMatch(matchId: string): Observable<PaymentMatch> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/matches/${matchId}/reject`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<PaymentMatch>(url, null, options);
  }

  approveCategorization(categorizationId: string): Observable<PaymentCategorization> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/categorizations/${categorizationId}/approve`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<PaymentCategorization>(url, null, options);
  }

  rejectCategorization(categorizationId: string): Observable<PaymentCategorization> {
    const url = this.utils.buildUrl(`admin/usr/payment-matching/categorizations/${categorizationId}/reject`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.post<PaymentCategorization>(url, null, options);
  }
}
